import { checkBackendReachable } from '@/services/healthClient';
import * as Sentry from '@sentry/vue';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, ref } from 'vue';

export type ConnectivityState = 'checking' | 'online' | 'offline' | 'retrying';

export type HeartbeatMode = 'normal' | 'unstable';

const AGGRESSIVE_RETRY_COUNT = 3;
const AGGRESSIVE_RETRY_DELAY_MS = 2000;
const STEADY_RETRY_DELAY_MS = 5000;
const HEARTBEAT_NORMAL_MS = 45_000;
const HEARTBEAT_UNSTABLE_MIN_MS = 5_000;
const HEARTBEAT_UNSTABLE_MAX_MS = 10_000;
const HEARTBEAT_FLAKE_TTL_MS = 30_000;
const STABILITY_THRESHOLD = 5;

// Tracks the currently-mounted store's dispose() so the module-level HMR hook
// can clean up listeners/timers before the new module instance runs.
let activeDispose: (() => void) | null = null;

export const useConnectivityStore = defineStore('connectivity', () => {
    const state = ref<ConnectivityState>('checking');
    const retryCount = ref(0);
    const lastAttemptAt = ref<number | null>(null);
    const hasEverConnected = ref(false);
    const heartbeatMode = ref<HeartbeatMode>('unstable');
    const consecutiveSuccessfulHeartbeats = ref(0);
    const lastFlakeAt = ref<number | null>(null);

    let activeLoop: Promise<void> | null = null;
    let heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
    let visibilityListenerAttached = false;
    let networkStatusListenerAttached = false;
    let heartbeatInFlight = false;
    let wakeRetryLoop: (() => void) | null = null;
    let started = false;
    let disposed = false;

    function sleepInterruptible(ms: number): Promise<void> {
        return new Promise((resolve) => {
            const timer = setTimeout(() => {
                wakeRetryLoop = null;
                resolve();
            }, ms);
            wakeRetryLoop = () => {
                clearTimeout(timer);
                wakeRetryLoop = null;
                resolve();
            };
        });
    }

    const isOnline = computed(() => state.value === 'online');
    const isBlocking = computed(() => state.value !== 'online');

    function nextDelayMs(attempt: number): number {
        if (attempt === 0) {
            return 0;
        }
        if (attempt < AGGRESSIVE_RETRY_COUNT) {
            return AGGRESSIVE_RETRY_DELAY_MS;
        }
        return STEADY_RETRY_DELAY_MS;
    }

    async function checkOnce(): Promise<boolean> {
        lastAttemptAt.value = Date.now();
        return checkBackendReachable();
    }

    async function runRetryLoop(): Promise<void> {
        let attempt = 0;
        while (!disposed) {
            const delay = nextDelayMs(attempt);
            if (delay > 0) {
                await sleepInterruptible(delay);
            }
            if (disposed) {
                return;
            }

            state.value = attempt === 0 ? 'checking' : 'retrying';

            const connected = await checkOnce();
            if (disposed) {
                return;
            }
            attempt += 1;
            retryCount.value = attempt;

            if (connected) {
                if (state.value !== 'online') {
                    Sentry.addBreadcrumb({
                        category: 'connectivity',
                        message: `Connectivity restored after ${attempt} attempt(s)`,
                        level: 'info',
                    });
                }
                hasEverConnected.value = true;
                state.value = 'online';
                retryCount.value = 0;
                consecutiveSuccessfulHeartbeats.value = 0;
                lastFlakeAt.value = null;
                startHeartbeat();
                return;
            }

            state.value = 'retrying';
        }
    }

    function ensureLoop(): Promise<void> {
        if (!activeLoop) {
            activeLoop = runRetryLoop()
                .catch((error) => Sentry.captureException(error))
                .finally(() => {
                    activeLoop = null;
                });
        }
        return activeLoop;
    }

    function waitForConnection(): Promise<void> {
        return ensureLoop();
    }

    function markOffline(reason?: string): void {
        if (disposed) {
            return;
        }
        pauseHeartbeat();

        if (state.value === 'offline' || state.value === 'retrying' || state.value === 'checking') {
            ensureLoop();
            return;
        }

        Sentry.addBreadcrumb({
            category: 'connectivity',
            message: `Connectivity lost${reason ? `: ${reason}` : ''}`,
            level: 'warning',
        });

        state.value = 'offline';
        retryCount.value = 0;
        ensureLoop();
    }

    // Heartbeat scheduler.
    //
    // Invariant: the scheduler is passive when `state !== 'online'`. It is
    // started exclusively by `runRetryLoop()`'s success branch and stopped
    // exclusively by `markOffline()`. It never self-resurrects via watchers.
    //
    // Mode is a real ref, written only by `recomputeMode()`. `recomputeMode()`
    // is called from each event that can change its inputs:
    //   - `notifyNetworkFlake()`              (lastFlakeAt changes)
    //   - `runHeartbeat` success branch       (streak += 1)
    //   - `runHeartbeat` failure branch       (streak = 0)
    //   - visibility-change handler (visible) (TTL may have elapsed while hidden)
    //
    // `heartbeatInFlight` guards against re-entry. `runHeartbeat()` is the only
    // site that flips it; both `runHeartbeat()` and `startHeartbeat()` consult
    // it to avoid leaking a `setTimeout` handle when an in-flight heartbeat's
    // success branch later schedules its own next tick.

    function recomputeMode(): void {
        const flakeAt = lastFlakeAt.value;
        const inFlakeWindow = flakeAt !== null && Date.now() - flakeAt < HEARTBEAT_FLAKE_TTL_MS;

        heartbeatMode.value = consecutiveSuccessfulHeartbeats.value >= STABILITY_THRESHOLD && !inFlakeWindow ? 'normal' : 'unstable';
    }

    function nextHeartbeatDelay(): number {
        if (heartbeatMode.value === 'normal') {
            return HEARTBEAT_NORMAL_MS;
        }
        const span = HEARTBEAT_UNSTABLE_MAX_MS - HEARTBEAT_UNSTABLE_MIN_MS;
        return HEARTBEAT_UNSTABLE_MIN_MS + Math.random() * span;
    }

    function scheduleNextHeartbeat(): void {
        if (disposed || state.value !== 'online') {
            return;
        }
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            return;
        }
        heartbeatTimer = setTimeout(runHeartbeat, nextHeartbeatDelay());
    }

    async function runHeartbeat(): Promise<void> {
        if (disposed || heartbeatInFlight) {
            return;
        }
        heartbeatInFlight = true;
        heartbeatTimer = null;

        try {
            const ok = await checkOnce();

            if (disposed || state.value !== 'online') {
                // disposed: lifecycle ended while we were awaiting.
                // not online: markOffline ran while we were awaiting; the retry loop drives recovery.
                return;
            }

            if (ok) {
                consecutiveSuccessfulHeartbeats.value += 1;
                recomputeMode();
                scheduleNextHeartbeat();
                return;
            }

            consecutiveSuccessfulHeartbeats.value = 0;
            recomputeMode();
            markOffline('heartbeat failed');
        } finally {
            heartbeatInFlight = false;
        }
    }

    function startHeartbeat(): void {
        if (disposed || heartbeatTimer !== null || heartbeatInFlight) {
            return;
        }
        recomputeMode();
        scheduleNextHeartbeat();
    }

    function pauseHeartbeat(): void {
        if (heartbeatTimer !== null) {
            clearTimeout(heartbeatTimer);
            heartbeatTimer = null;
        }
    }

    function notifyNetworkFlake(): void {
        if (disposed) {
            return;
        }
        lastFlakeAt.value = Date.now();
        recomputeMode();
    }

    function handleVisibilityChange(): void {
        if (typeof document === 'undefined') {
            return;
        }

        if (document.visibilityState === 'hidden') {
            pauseHeartbeat();
            return;
        }

        // visible
        if (state.value !== 'online') {
            // Retry loop owns recovery; it will start the heartbeat on reconnect.
            return;
        }
        if (heartbeatTimer !== null) {
            // Already armed (e.g. duplicate event); nothing to do.
            return;
        }

        // Re-evaluate mode (the flake TTL may have elapsed during the hidden
        // period), then run an immediate check; runHeartbeat() reschedules on
        // completion.
        recomputeMode();
        void runHeartbeat();
    }

    function ensureVisibilityListener(): void {
        if (visibilityListenerAttached) {
            return;
        }
        if (typeof document === 'undefined') {
            return;
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);
        visibilityListenerAttached = true;
    }

    // navigator.onLine === true does not guarantee reachability, so we still
    // probe via checkOnce(). 'online' just wakes the retry loop's sleep so we
    // verify immediately instead of waiting out the 2s/5s cadence.
    function handleOnline(): void {
        wakeRetryLoop?.();
    }

    function handleOffline(): void {
        markOffline('navigator offline');
    }

    function ensureNetworkStatusListener(): void {
        if (networkStatusListenerAttached) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        networkStatusListenerAttached = true;
    }

    function detachVisibilityListener(): void {
        if (!visibilityListenerAttached) {
            return;
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
        visibilityListenerAttached = false;
    }

    function detachNetworkStatusListener(): void {
        if (!networkStatusListenerAttached) {
            return;
        }
        if (typeof window !== 'undefined') {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        }
        networkStatusListenerAttached = false;
    }

    // Lifecycle.
    //
    // `start()` is the single attachment point for window/document listeners
    // and the entry point for the retry loop. `dispose()` tears everything
    // down. `disposed` is terminal within a closure instance — a re-started
    // store comes from a fresh factory invocation (Pinia HMR via
    // `acceptHMRUpdate`), not from flipping the flag back.

    function start(): void {
        if (started || disposed) {
            return;
        }
        started = true;
        ensureNetworkStatusListener();
        ensureVisibilityListener();
        ensureLoop();
    }

    function dispose(): void {
        if (disposed) {
            return;
        }
        disposed = true;
        started = false;

        pauseHeartbeat();
        // Wake any in-flight sleep so the retry loop reaches its `disposed` check.
        wakeRetryLoop?.();

        detachNetworkStatusListener();
        detachVisibilityListener();

        if (activeDispose === dispose) {
            activeDispose = null;
        }
    }

    activeDispose = dispose;

    return {
        state,
        retryCount,
        lastAttemptAt,
        hasEverConnected,
        heartbeatMode,
        isOnline,
        isBlocking,
        start,
        dispose,
        waitForConnection,
        markOffline,
        notifyNetworkFlake,
        checkConnection: checkOnce,
    };
});

if (import.meta.hot) {
    // Tear the active instance down before Vite swaps in the new module.
    // `acceptHMRUpdate` then lets Pinia re-run the factory against the same
    // store id, producing fresh closures (timer handles, attached flags,
    // disposed flag) so the new instance starts clean.
    import.meta.hot.dispose(() => activeDispose?.());
    import.meta.hot.accept(acceptHMRUpdate(useConnectivityStore, import.meta.hot));
}
